<template>
  <b-card>
    <b-row>
      <b-col cols="12">
        <h2 class="text-blue font-weight-bold mt-1">
          {{ $t('email_logs') }}
        </h2>
      </b-col>
    </b-row>
    <b-row class="email-logs-filters mb-3 align-items-end">
      <b-col cols="12" sm="6" md="2">
        <b-form-group
          :label="$t('start_date') + ':'"
          label-for="email-logs-start-date"
          class="mb-0"
        >
          <b-form-input
            id="email-logs-start-date"
            v-model.trim="searchData.start_date"
            type="date"
          />
        </b-form-group>
      </b-col>
      <b-col cols="12" sm="6" md="2">
        <b-form-group
          :label="$t('end_date') + ':'"
          label-for="email-logs-end-date"
          class="mb-0"
        >
          <b-form-input
            id="email-logs-end-date"
            v-model.trim="searchData.end_date"
            type="date"
          />
        </b-form-group>
      </b-col>
      <b-col cols="12" sm="6" md="4" lg="3">
        <b-form-group
          :label="$t('filter') + ':'"
          label-for="email-logs-search"
          class="mb-0"
        >
          <b-form-input
            id="email-logs-search"
            v-model.trim="searchText"
            type="search"
            :placeholder="$t('type_to_search')"
            @keyup.enter="applyFilters"
          />
        </b-form-group>
      </b-col>
      <b-col cols="12" lg="auto" class="d-flex align-items-end flex-wrap mb-2 mb-lg-0 email-logs-filters__actions">
        <b-button variant="secondary" class="mr-1 mb-2 mb-lg-0" @click="applyFilters">
          {{ $t('search') }}
        </b-button>
        <b-button variant="outline-secondary" class="mb-2 mb-lg-0" @click="clearFilters">
          {{ $t('clear') }}
        </b-button>
      </b-col>
    </b-row>
    <Table
      large
      :fields="fields"
      url="/email-logs"
      :show-search="false"
      :body="filterBody"
      :update-data="updateData"
      responsive="sm"
    >
      <template #cell(created_at)="data">
        <span v-if="data.item.created_at">
          {{ data.item.created_at | formatDate }}
        </span>
        <span v-else>—</span>
      </template>
      <template #cell(recipient_email)="data">
        <span class="text-nowrap">{{ data.item.recipient_email || '—' }}</span>
      </template>
      <template #cell(subject)="data">
        <span
          v-if="data.item.subject"
          class="text-truncate d-inline-block email-logs-subject"
          :title="data.item.subject"
        >
          {{ data.item.subject }}
        </span>
        <span v-else>—</span>
      </template>
      <template #cell(actions)="data">
        <b-button
          v-b-tooltip.hover
          variant="outline-primary"
          size="sm"
          :title="$t('show_email_content')"
          @click="showEmailContent(data.item.id)"
        >
          <feather-icon icon="EyeIcon" size="16" />
        </b-button>
      </template>
    </Table>

    <b-modal
      v-model="contentModalVisible"
      hide-footer
      :centered="false"
      modal-class="email-logs-modal"
      content-class="email-logs-modal__content"
      body-class="email-logs-modal__body p-0"
      header-class="email-logs-modal__header"
      @hidden="onContentModalHidden"
    >
      <div v-if="loadingContent" class="text-center py-3 px-3">
        {{ $t('loading') }}...
      </div>
      <template v-else-if="selectedLog">
        <iframe
          v-if="selectedLog.html_content"
          ref="emailPreviewFrame"
          class="email-logs-preview-frame"
          :srcdoc="selectedLog.html_content"
          :title="modalTitle"
          scrolling="no"
          @load="fitEmailPreviewFrame"
        />
        <p v-else class="text-muted mb-0 px-3 py-2">
          {{ selectedLog.template_name ? $t('email_preview_unavailable') : '—' }}
        </p>
      </template>
    </b-modal>
  </b-card>
</template>

<script>
import {
  BCard,
  BRow,
  BCol,
  BFormGroup,
  BFormInput,
  BButton,
  BModal,
  VBTooltip,
} from 'bootstrap-vue'
import Table from '@/components/Common/Table2.vue'

export default {
  name: 'EmailLogs',
  components: {
    BCard,
    BRow,
    BCol,
    BFormGroup,
    BFormInput,
    BButton,
    BModal,
    Table,
  },
  directives: {
    'b-tooltip': VBTooltip,
  },
  data() {
    return {
      updateData: 0,
      searchText: '',
      searchData: {
        start_date: '',
        end_date: '',
      },
      fields: [
        { key: 'created_at', label: 'time', sortable: true },
        { key: 'recipient_email', label: 'email', sortable: true },
        { key: 'subject', label: 'subject', sortable: true },
        { key: 'actions', label: 'actions', sortable: false },
      ],
      contentModalVisible: false,
      loadingContent: false,
      selectedLog: null,
    }
  },
  computed: {
    modalTitle() {
      return this.selectedLog?.subject || this.$t('email_content')
    },
    filterBody() {
      const body = {}
      if (this.searchData.start_date) {
        body.start_date = this.searchData.start_date
      }
      if (this.searchData.end_date) {
        body.end_date = this.searchData.end_date
      }
      if (this.searchText) {
        body.search = this.searchText
      }
      return body
    },
  },
  methods: {
    applyFilters() {
      this.updateData += 1
    },
    clearFilters() {
      this.searchText = ''
      this.searchData.start_date = ''
      this.searchData.end_date = ''
      this.updateData += 1
    },
    onContentModalHidden() {
      this.selectedLog = null
      this.loadingContent = false
    },
    async showEmailContent(id) {
      this.contentModalVisible = true
      this.loadingContent = true
      this.selectedLog = null
      try {
        const res = await this.$http.get(`/email-logs/${id}`)
        this.selectedLog = res?.data?.value || null
      } catch (e) {
        this.contentModalVisible = false
        this.$toast?.error?.(this.$t('error_loading_email_content'))
      } finally {
        this.loadingContent = false
        this.$nextTick(() => this.fitEmailPreviewFrame())
      }
    },
    fitEmailPreviewFrame() {
      const iframe = this.$refs.emailPreviewFrame
      const doc = iframe?.contentDocument
      if (!doc) {
        return
      }

      doc.documentElement.style.overflow = 'hidden'
      doc.documentElement.style.margin = '0'
      doc.body.style.overflow = 'hidden'
      doc.body.style.margin = '0'

      const height = Math.max(
        doc.documentElement.scrollHeight,
        doc.body.scrollHeight,
      )
      iframe.style.height = `${height}px`
    },
  },
}
</script>

<style scoped>
.email-logs-filters__actions {
  gap: 0.25rem;
}

.email-logs-subject {
  max-width: 420px;
}

.email-logs-preview-frame {
  display: block;
  width: 600px;
  max-width: 100%;
  margin: 0 auto;
  border: 0;
  overflow: hidden;
}
</style>

<style>
/* Modal is teleported to document body — global styles required */
.email-logs-modal {
  -ms-overflow-style: none;
  scrollbar-width: none;
}

.email-logs-modal::-webkit-scrollbar {
  display: none;
  width: 0;
  height: 0;
}

.email-logs-modal .modal-dialog {
  width: 640px;
  max-width: calc(100vw - 2rem);
  margin: 1.75rem auto;
}

.email-logs-modal__content,
.email-logs-modal__header,
.email-logs-modal .modal-header,
.email-logs-modal__header,
.email-logs-modal .modal-header,
.email-logs-modal__body,
.email-logs-modal .modal-body {
  background-color: #eceff1 !important;
  overflow: visible !important;
  max-height: none !important;
}

.email-logs-modal__content,
.email-logs-modal .modal-content {
  border: none;
}

.email-logs-modal .email-logs-html-content {
  overflow: visible;
  max-height: none;
}

.email-logs-modal .email-logs-html-content table {
  max-width: 100%;
}

.email-logs-modal .email-logs-html-content table[width="600"],
.email-logs-modal .email-logs-html-content table[style*="width: 600px"] {
  width: 600px !important;
  max-width: 100%;
  box-sizing: border-box;
}
</style>
